/**
 * El paso Prueba del asistente: mandarse el mensaje real antes de gastar en miles, cómo salió (`TarjetaPrueba`) y
 * «Seguir sin probar». El envío, la consulta del estado y lo que deja seguir viven en `usePruebaCampana`.
 *
 * La prueba es de ESTA campaña: la de otra nunca se toma sola. Lo que se sabe de otra se dice (`deOtra`: una copia
 * empieza de cero; `deLaSerie`: una repetición, al lado de «Seguir sin probar»), y nada deja seguir sin una prueba que
 * llegó o sin marcar «Seguir sin probar».
 */
import { Link } from "react-router-dom";
import { FlaskConical, Wallet } from "lucide-react";
import type { ModoTransporte, PlantillaDetalle, ResultadoPrueba } from "../../types";
import type { tituloPrueba } from "../../logic/chequeo";
import { ESTADO_MENSAJE, avisoSaldoPrueba } from "../../logic/textos";
import { usdPorMensaje } from "../../logic/formato";
import type { Aviso } from "../../logic/asistente";
import { AvisoRojo } from "./AvisoRojo";
import { FormPrueba } from "./FormPrueba";
import { TarjetaPrueba } from "./TarjetaPrueba";

const RUTA_CONFIG = "/activa/oficial/configuracion";

export function PasoPrueba({
  costoPorMensajeUSD, saldoUSD, modo, numero, onNumero, enviando, onEnviar, error, vista, titulo, previa,
  consultando, vencida, pollDetenido, onReconsultar, sinPrueba, onSinPrueba, deOtra, deLaSerie,
}: {
  /** lo que cuesta un mensaje de la plantilla elegida, con el recargo: lo mismo que la prueba */
  costoPorMensajeUSD: number | undefined;
  /** el saldo de la cuenta (null mientras carga) */
  saldoUSD: number | null | undefined;
  modo: ModoTransporte | null;
  numero: string; onNumero: (v: string) => void; enviando: boolean; onEnviar: () => void; error: Aviso | null;
  /** la prueba mandada con su último estado (null antes de mandar) y su título (`tituloPrueba`) */
  vista: ResultadoPrueba | null; titulo: ReturnType<typeof tituloPrueba> | null;
  /** la plantilla elegida, para la previa de la tarjeta */
  previa?: PlantillaDetalle;
  consultando: boolean; vencida: boolean; pollDetenido: boolean; onReconsultar: () => void;
  sinPrueba: boolean; onSinPrueba: (v: boolean) => void;
  /** lo que se sabe de la prueba de otra campaña (es una copia): se dice antes de mandar la de ésta */
  deOtra?: string | null;
  /** una repetición: si la serie ya se probó con esta plantilla, y cuándo (no la reemplaza: es un dato para decidir) */
  deLaSerie?: string | null;
}) {
  // El mismo costo que muestra el paso (el máximo de un mensaje con el recargo) contra el saldo de la cuenta.
  const avisoSaldo = avisoSaldoPrueba({ saldoUSD, costoUSD: costoPorMensajeUSD });
  // la última prueba falló: mandar igual es mandar sin probar, y se elige sabiéndolo
  const fallo = !!vista && titulo?.tono === "error";
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700">
        Mandate el mensaje real a tu teléfono antes de gastar en miles. Cuesta como un mensaje: {usdPorMensaje(costoPorMensajeUSD)}.
      </p>
      {deOtra && !vista && <p className="text-sm text-slate-600" data-testid="prueba-de-otra">{deOtra}</p>}
      {modo === "simulated" && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <FlaskConical size={14} className="mt-0.5 flex-shrink-0" /> En modo simulado la prueba no llega a ningún teléfono: se registra como si hubiera salido.
        </p>
      )}
      {/* Antes de tocar «Mandar prueba»: si el saldo no cubre lo que cuesta, se dice cuánto cargar y dónde. El
          botón queda prendido: el saldo de la pantalla puede ser de antes de una recarga en otra pestaña, y el
          backend corta igual (con el monto) si no alcanza. */}
      {avisoSaldo && !vista && (
        <p role="status" className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <Wallet size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{avisoSaldo} <Link to={`${RUTA_CONFIG}?seccion=saldo`} className="font-semibold underline">Cargar saldo</Link></span>
        </p>
      )}
      <FormPrueba numero={numero} onNumero={onNumero} enviando={enviando} onEnviar={onEnviar} />
      <p className="text-xs text-slate-500">Con los datos de un contacto de la audiencia.</p>
      {error && <AvisoRojo aviso={error} />}
      {vista && titulo && (
        <TarjetaPrueba
          titulo={titulo}
          estadoLabel={ESTADO_MENSAJE[vista.estado]?.label || vista.estado}
          costoReservadoUSD={vista.costoReservadoUSD}
          texto={vista.texto}
          previa={previa}
          consultando={consultando}
          vencida={vencida}
          pollDetenido={pollDetenido}
          onReconsultar={onReconsultar}
        />
      )}
      <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700">
        <input type="checkbox" checked={sinPrueba} onChange={(e) => onSinPrueba(e.target.checked)} className="mt-0.5 accent-violet-600" />
        <span>
          Seguir sin probar
          <span className="block text-xs text-slate-500">Si hay un error de variables lo vas a ver en todos los mensajes.</span>
          {fallo && (
            <span className="mt-0.5 block text-xs font-semibold text-red-700" data-testid="prueba-fallo-sin-probar">
              La prueba falló: si la mandás igual, sale sin probar.
            </span>
          )}
          {deLaSerie && <span className="mt-0.5 block text-xs font-medium text-slate-700" data-testid="prueba-de-la-serie">{deLaSerie}</span>}
        </span>
      </label>
    </div>
  );
}
